import { FormEvent, useId, useRef, useState } from 'react';
import axios from 'axios';
import { Dialog } from './dialogs/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useAuth } from '../providers/auth-provider';
import { useApi } from '../providers/api-provider';
import { useUserStore } from './zustand-stores';
import { ContactUsValues, contactUsLimits, validateContactUs } from '../general/contact-us';

export function ContactUsDialog({ onHide }: { onHide: () => void }) {
    const api = useApi();
    const auth = useAuth();
    const account = auth.getIsAuthenticated() ? auth.getUser() : undefined;
    const profile = useUserStore(state => state.user);
    const [values, setValues] = useState<ContactUsValues>(() => ({
        name: account ? (profile ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim()
            : `${account.given_name ?? ''} ${account.family_name ?? ''}`.trim() || account.name || '') : '',
        email: account ? profile?.email || account.email || '' : '', subject: '', message: '', company: '',
    }));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [attempted, setAttempted] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const inFlight = useRef(false);
    const form = useRef<HTMLFormElement>(null);
    const id = useId();
    const update = (key: keyof ContactUsValues, value: string) => {
        const next = { ...values, [key]: value };
        setValues(next); setError('');
        if (attempted) setErrors(validateContactUs(next));
    };
    const send = async (event: FormEvent) => {
        event.preventDefault();
        if (inFlight.current) return;
        const validation = validateContactUs(values);
        setAttempted(true); setErrors(validation); setError('');
        if (Object.keys(validation).length) {
            form.current?.ownerDocument.getElementById(`${id}-${Object.keys(validation)[0]}`)?.focus();
            return;
        }
        inFlight.current = true; setSending(true);
        try {
            if (!api) throw new Error('API is initializing');
            await api.submitContactUs(values);
            setSent(true);
        } catch (failure) {
            const data = axios.isAxiosError(failure) ? failure.response?.data : null;
            if (data?.errors && typeof data.errors === 'object') {
                const known: Record<string, string> = {};
                for (const key of Object.keys(contactUsLimits)) if (typeof data.errors[key] === 'string') known[key] = data.errors[key];
                setErrors(known);
            }
            setError(axios.isAxiosError(failure) && failure.response?.status === 429
                ? 'Too many messages. Please wait a few minutes before trying again. Your message has been kept.'
                : 'Your message could not be sent. Your message has been kept; please try again later.');
        } finally { inFlight.current = false; setSending(false); }
    };
    return <Dialog header="Send an email" visible modal draggable={false} closable={!sending} closeOnEscape={!sending}
        onHide={() => { if (!sending) onHide(); }} style={{ width: '36rem', maxWidth: 'calc(100vw - 2rem)' }}>
        {sent ? <div role="status"><p className="mt-0">Thank you. Your message has been sent.</p><div className="flex justify-content-end"><Button label="Close" onClick={onHide}/></div></div>
        : <form ref={form} onSubmit={send} noValidate aria-label="Send an email">
            <p className="mt-0 text-color-secondary">Send an enquiry and receive a reply by email. All fields are required.</p>
            <div className="flex flex-column gap-3">
                {(Object.keys(contactUsLimits) as (keyof typeof contactUsLimits)[]).map(key => <div key={key}>
                    <label htmlFor={`${id}-${key}`} className="block font-semibold mb-2">{({name:'Name',email:'Email',subject:'Subject',message:'Message'})[key]}</label>
                    {key === 'message' ? <InputTextarea id={`${id}-${key}`} value={values[key]} rows={6} maxLength={contactUsLimits[key]}
                        className={`w-full${errors[key] ? ' p-invalid' : ''}`} disabled={sending} aria-required aria-invalid={!!errors[key]}
                        aria-describedby={errors[key] ? `${id}-${key}-error` : undefined} onChange={event => update(key,event.target.value)}/>
                    : <InputText id={`${id}-${key}`} type={key === 'email' ? 'email' : 'text'} value={values[key]} maxLength={contactUsLimits[key]}
                        autoComplete={key === 'name' ? 'name' : key === 'email' ? 'email' : 'off'} className={`w-full${errors[key] ? ' p-invalid' : ''}`}
                        disabled={sending} aria-required aria-invalid={!!errors[key]} aria-describedby={errors[key] ? `${id}-${key}-error` : undefined}
                        onChange={event => update(key,event.target.value)}/>}
                    {errors[key] && <small id={`${id}-${key}-error`} className="p-error block mt-1">{errors[key]}</small>}
                </div>)}
            </div>
            <div aria-hidden="true" style={{position:'absolute',left:'-10000px',width:'1px',height:'1px',overflow:'hidden'}}>
                <label htmlFor={`${id}-company`}>Leave this field empty</label>
                <input id={`${id}-company`} name="company" tabIndex={-1} autoComplete="off" data-word-completion="off" value={values.company} onChange={event=>update('company',event.target.value)}/>
            </div>
            {error && <p role="alert" className="p-error">{error}</p>}
            <div className="flex justify-content-end gap-2 mt-4"><Button type="button" label="Cancel" text disabled={sending} onClick={onHide}/>
                <Button type="submit" label="Send" icon="pi pi-send" loading={sending} disabled={sending}/></div>
        </form>}
    </Dialog>;
}
